import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Divider,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import CategoriesSelect from "../components/Categories/CategoriesSelect";

const toSlug = (text) =>
  text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9\-]/g, "");

const CategoriesPage = () => {
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const navigate = useNavigate();
  const { sectionSlug, categorySlug } = useParams();

  useEffect(() => {
    document.title = "Tienda | D&R Soluciones Informáticas";
    const fetchSections = async () => {
      try {
        const res = await fetch("/categorias/secciones/");
        if (!res.ok) throw new Error();
        const data = await res.json();
        setSections(data);

        if (sectionSlug && categorySlug) {
          const sec = data.find((s) => toSlug(s.name) === sectionSlug);
          const cat = sec?.categories.find(
            (c) => toSlug(c.name) === categorySlug
          );
          if (sec && cat) {
            setSelectedSection(sec);
            setSelectedCategory(cat);
          }
        } else {
          setSelectedSection(null);
          setSelectedCategory(null);
        }
      } catch (e) {
        console.error(e);
      }
    };
    fetchSections();
  }, [sectionSlug, categorySlug]);

  const handleCategoryClick = (sec, cat) => {
    const sSlug = toSlug(sec.name);
    const cSlug = toSlug(cat.name);
    navigate(`/categorias/${sSlug}/${cSlug}`);
  };

  return (
    <Box sx={{ p: 4, bgcolor: "#f5f5f5" }}>
      {selectedCategory ? (
        <CategoriesSelect
          section={selectedSection}
          category={selectedCategory}
          setSelectedCategory={setSelectedCategory}
        />
      ) : (
        sections.length > 0 && (
          <Paper elevation={3} sx={{ p: 3, borderRadius: 3, bgcolor: "#fff" }}>
            {sections.map((sec, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.15 }}
              >
                <Box sx={{ mb: 4 }}>
                  
                  <Grid container spacing={2} justifyContent="center">
                    <Grid item xs={12} md={4} sx={{ display: "flex", justifyContent: "center" }}>
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          minHeight: { xs: 180, sm: 200 },
                        }}
                      >
                        {sec.image && (
                          <Box
                            component="img"
                            src={sec.image}
                            alt={sec.name}
                            sx={{
                              width: { xs: "90%", sm: "auto" },
                              height: "100%",
                              maxHeight: { xs: 400, sm: 450 },
                              objectFit: "contain",
                              py: 1,
                              display: "block",
                            }}
                          />
                        )}
                      </Box>
                    </Grid>

                    <Grid item xs={12} sx={{ textAlign: "center", mt: 1 }}>
                      <Typography
                        variant="h2"
                        sx={{
                          fontSize: { xs: "1.3rem", sm: "1.5rem", md: "1.7rem" },
                          fontWeight: "bold",
                          color: "#070707ff",
                          mb: 2,
                        }}
                      >
                        {sec.name}
                      </Typography>

                      <Typography
                        sx={{
                          fontWeight: "bold",
                          fontSize: { xs: "1rem", sm: "1.1rem" },
                          color: "#0A3D91",
                          mb: 2,
                        }}
                      >
                        CATEGORÍAS
                      </Typography>

                      <Grid container spacing={1} justifyContent="center">
                        {sec.categories.map((cat, idx) => (
                          <Grid item xs={6} sm={4} md={3} key={idx}>
                            <Box
                              onClick={() => handleCategoryClick(sec, cat)}
                              sx={{
                                textAlign: "center",
                                color: "#232323ff",
                                cursor: "pointer",
                                fontSize: {
                                  xs: "0.9rem",
                                  sm: "1rem",
                                  md: "1.2rem",
                                },
                                py: 0.5,
                                "&:hover": {
                                  color: "#0D6EFD",
                                  fontWeight: "bold",
                                },
                              }}
                            >
                              {cat.name}
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  </Grid>

                  {i < sections.length - 1 && (
                    <Divider sx={{ my: 3, bgcolor: "#ddd" }} />
                  )}
                </Box>
              </motion.div>
            ))}
          </Paper>
        )
      )}
    </Box>
  );
};

export default CategoriesPage;
